import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import GoogleMapReact from 'google-map-react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Grid,
  Modal,
  Link,
  Skeleton,
  Alert,
  LinearProgress,
} from '@mui/material';
import {
  countryListWithCode,
  MARKET_FINDER_COLUMN_METADATA,
  MONTHLY_BEST_MARKET_FINDER_LIMIT,
  SAVE_USER_DATA_LAMBDA_URL,
  usStateListWithCode,
  macro_to_subgroups,
  macro_group_legends,
  sub_group_legends,
  subGroupToCountryCodes,
  DEFAULT_SELECTED_COLUMNS_EXPLORER,
  MARKET_EXPLORER_EXPORT_LIMIT_ROW_MONTHLY,
  exchangeRates,
} from 'src/utils/const';
import { GOOGLE_MAPS_API_KEY } from 'src/utils/keys';
import { callApi } from 'src/core/callApi';
import { User } from 'src/interfaces/iuser';
import _ from 'lodash';
import {
  convertToUSD,
  convertCurrency,
  makeDownloadableBlob,
  formatMarketName,
  mapOptions,
  getCoordinatesFromWKT,
  getGradeColor,
  abbreviateNumber,
  isWithinBounds,
} from 'src/utils/functions';
import FilterBar from './FilterBar';
import SelectedFiltersDisplay from './SelectedFiltersDisplay';
import MarketDataGrid, { MarketDataRow } from './MarketDataGrid';
import { useCanAccessExpertPlan, useCanAccessScalePlan } from 'src/hooks/useFeatureAccess';
import { useDispatch, useSelector } from 'react-redux';
import { LoadingCard } from 'src/App/Dashboards/Market/Components';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {
  getUserInfo,
  initializeSavedMarketsData,
  setSelectedListLoadingAction,
  getMarketsInMapViewport,
} from 'src/store/Dashboard/actions';
import AutohideSnackbar from 'src/App/Dashboards/Market/Components/Alerts/AutohideSnackbar';
import styles from './style';
import MarketPointer from 'src/App/Dashboards/Market/Components/Pointer/MarketPointer';
import { COLOR_PRIMARY_2 } from 'src/utils/colors';
import { ShowFullMapButton } from 'src/App/Dashboards/Market/Components/Buttons';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import TooltipNew from 'src/App/Dashboards/Market/Components/Tooltips/TooltipNew';
import TutorialModal from 'src/App/Dashboards/Market/Modals/TutorialModal';
import MarketExportModal from './MarketExportModal';
import { updateUserExplorerPageRowDataExportUsage } from './helper';
import { Search, Bounds, MarketInMapViewport } from 'src/interfaces/idashboard';
import { TwoPaneMapTableLayout } from 'src/App/Dashboards/Market/Layouts';
import { getSubmarketColor } from 'src/App/Dashboards/Market/Pages/OverviewPage/helper';
import MapComponent from './MapComponent';
import RateLimitAlert from '../../Components/Alerts/RateLimitAlert';

// Define blur states
type BlurState = 'none' | 'blurMarket' | 'blurAll' | 'blurPartial';

// Define type for market data with blur state by intersecting with MarketDataRow
type MarketDataWithBlur = MarketDataRow & {
  _blurState: BlurState;
};

const filterIconStyle = {
  fontSize: '16px',
};

// ===============================
// Filter Options (Moved from FilterBar)
// ===============================
const regulationOptions = [
  { value: 'strict', label: 'Strict', helperText: '(Required short-term rental license)' },
  { value: 'lenient', label: 'Lenient', helperText: '(May or may not require license)' },
];
const marketSizeOptions = [
  { value: 'big', label: 'Large', helperText: '(Over 5,000 active listings)' },
  { value: 'medium', label: 'Medium', helperText: '(1,000-5,000 active listings)' },
  { value: 'small', label: 'Small', helperText: '(Less than 1,000 active listings)' },
];
const sortByOptions = [
  {
    value: 'supply',
    label: 'Most Listings',
    icon: <HomeOutlinedIcon sx={filterIconStyle} />,
    tooltip: 'Shows markets with the highest number of active short-term rental listings',
  },
  {
    value: 'market_grade',
    label: 'Airbtics Ranked',
    icon: <EmojiEventsIcon sx={filterIconStyle} />,
    tooltip:
      'Shows the most attractive short-term rental investment opportunities at a glance. High yield, positive trend, and favorable regulation.',
  },
  {
    value: 'rent_gap',
    label: 'Top Rental Arbitrage',
    icon: <AttachMoneyOutlinedIcon sx={filterIconStyle} />,
    tooltip:
      'Markets with high long-term rental and short-term rental differences - ideal for Airbnb Arbitrage.',
  },
  {
    value: 'short_term_rental_yield',
    label: 'Top Investments',
    icon: <AccountBalanceWalletOutlinedIcon sx={filterIconStyle} />,
    tooltip: 'Markets with high gross rental yields. High revenue and low property prices.',
  },
  // { value: 'revenue', label: 'Revenue', tooltip: '' },
  {
    value: 'yoy_supply_change',
    label: 'Getting Saturated',
    icon: <BarChartOutlinedIcon sx={filterIconStyle} />,
    tooltip: 'Markets with the highest year-over-year increase in Airbnb supply.',
  },
  {
    value: 'yoy_revenue_change',
    label: 'Up & Coming',
    icon: <RocketLaunchOutlinedIcon sx={filterIconStyle} />,
    tooltip:
      'Markets with the highest year-over-year increase in average short-term rental revenue.',
  },
];

// Transform country list and add US states
const countryOptions = (() => {
  const options: any[] = [];

  Object.entries(macro_to_subgroups).forEach(([macro, subgroups]) => {
    const prefixedMacroValue = `macro-${macro}`;
    const macroOption = {
      value: prefixedMacroValue,
      label: macro_group_legends[macro],
      isExpandable: true, // Macros are always expandable to show subgroups or direct countries
      parentValue: null as string | null,
      isChild: false,
      level: 0,
    };
    options.push(macroOption);
    const seenValuesForMacro = new Set(); // Deduplication at the macro level

    if (subgroups.length === 1 && subgroups[0] === macro) {
      // Case: Macro is its own subgroup (e.g., NA: ['NA'])
      // Countries are direct children of the macro.
      const countryCodes = Array.from(new Set(subGroupToCountryCodes[macro] || []));
      countryCodes.forEach(countryCode => {
        const prefixedCountryValue = `country-${countryCode}`;
        if (seenValuesForMacro.has(prefixedCountryValue)) return;
        seenValuesForMacro.add(prefixedCountryValue);

        const country = countryListWithCode.find(c => c.code === countryCode);
        const isUS = countryCode === 'US';
        const countryLabel = country ? country.label.trim() : countryCode;

        const countryOption = {
          value: prefixedCountryValue,
          label: countryLabel,
          isExpandable: isUS,
          parentValue: prefixedMacroValue, // Parent is the macro itself
          isChild: true,
          level: 1, // Level 1, directly under macro
        };
        options.push(countryOption);

        if (isUS) {
          usStateListWithCode.forEach(state => {
            const prefixedStateValue = `state-${state.code}`;
            if (seenValuesForMacro.has(prefixedStateValue)) return;
            seenValuesForMacro.add(prefixedStateValue);
            options.push({
              value: prefixedStateValue,
              label: state.label,
              isExpandable: false,
              parentValue: prefixedCountryValue, // Parent is 'country-US'
              isChild: true,
              isState: true,
              level: 2, // Level 2, under 'country-US' (which is L1 relative to macro)
            });
          });
        }
      });
    } else {
      // Case: Macro has distinct subgroups (e.g., MEA: ['MENA', 'SSA'])
      // Filter out the macro itself if it's mistakenly listed as one of its own distinct subgroups
      subgroups
        .filter(subgroupItem => subgroupItem !== macro)
        .forEach(subgroupItem => {
          const prefixedSubgroupValue = `subgroup-${subgroupItem}`;
          const subgroupLabel = sub_group_legends[subgroupItem] || subgroupItem;
          const subgroupOption = {
            value: prefixedSubgroupValue,
            label: subgroupLabel,
            isExpandable: true, // Subgroups are expandable to show countries
            parentValue: prefixedMacroValue,
            isChild: false, // This is a category (parent for countries)
            level: 1,
          };
          options.push(subgroupOption);

          const countryCodes = Array.from(new Set(subGroupToCountryCodes[subgroupItem] || []));
          countryCodes.forEach(countryCode => {
            const prefixedCountryValue = `country-${countryCode}`;
            if (seenValuesForMacro.has(prefixedCountryValue)) return;
            seenValuesForMacro.add(prefixedCountryValue);

            const country = countryListWithCode.find(c => c.code === countryCode);
            const isUS = countryCode === 'US';
            const countryLabel = country ? country.label.trim() : countryCode;

            const countryOption = {
              value: prefixedCountryValue,
              label: countryLabel,
              isExpandable: isUS,
              parentValue: prefixedSubgroupValue, // Parent is the subgroup
              isChild: true,
              level: 2, // Level 2, under subgroup
            };
            options.push(countryOption);

            if (isUS) {
              usStateListWithCode.forEach(state => {
                const prefixedStateValue = `state-${state.code}`;
                if (seenValuesForMacro.has(prefixedStateValue)) return;
                seenValuesForMacro.add(prefixedStateValue);
                options.push({
                  value: prefixedStateValue,
                  label: state.label,
                  isExpandable: false,
                  parentValue: prefixedCountryValue, // Parent is 'country-US'
                  isChild: true,
                  isState: true,
                  level: 3, // Level 3, under 'country-US' (which is L2 relative to subgroup)
                });
              });
            }
          });
        });
    }
  });
  return options;
})();

// Helper to find label for a given value in options array
const findLabel = (
  options: { value: string; label: string }[], // Simplified, as specific child/parent logic isn't needed here anymore
  value: string
): string => {
  const option = options.find(opt => opt.value === value);
  return option ? option.label : '';
};

const CreditWarningModal = ({
  open,
  handleClose,
  handleOnClick,
  handleOnSampleClick,
}: {
  open: boolean;
  handleClose: () => void;
  handleOnClick: () => Promise<void>;
  handleOnSampleClick: (sample: string) => void;
}) => {
  const user: User = useSelector(state => (state as any).dashboard.user);
  const bestMarketFinderUsage = user.bestMarketFinderUsage;

  // YYYY-MM
  const currentYearMonthString = new Date().toISOString().slice(0, 7);
  const creditsUsed = bestMarketFinderUsage[currentYearMonthString] || 0;
  const totalCredits = MONTHLY_BEST_MARKET_FINDER_LIMIT[user.subscriptionPlan];
  const creditsRemaining = Math.max(0, totalCredits - creditsUsed);

  const isScaleUser = useCanAccessScalePlan();

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'min(max(300px, 90vw), 500px)',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: '30px',
          borderRadius: '12px',
          outline: 'none',
        }}
      >
        <IconButton
          onClick={handleClose}
          sx={{
            position: 'absolute',
            top: '10px',
            right: '10px',
          }}
        >
          <CloseRoundedIcon />
        </IconButton>
        <Typography variant="h6" sx={{ mb: '20px' }}>
          {(creditsRemaining > 0 || isScaleUser) && (
            <>
              Searching for markets will use one credit. Proceed to confirm your search. You have{' '}
              {creditsRemaining} credits remaining for this month.
            </>
          )}

          {creditsRemaining <= 0 && !isScaleUser && (
            <>
              You’ve reached your Expert plan limit for Market Explorer searches.{' '}
              <Link
                href={undefined}
                onClick={() => {
                  handleOnSampleClick('scale');
                  handleClose();
                }}
                sx={{
                  color: COLOR_PRIMARY_2,
                  cursor: 'pointer',
                }}
              >
                Upgrade to Scale Plan
              </Link>{' '}
              for 15 additional searches or wait for your limit to reset next month.
            </>
          )}
        </Typography>
        <Button
          variant="contained"
          disabled={creditsRemaining <= 0 && isScaleUser}
          onClick={() => {
            if (isScaleUser || creditsRemaining > 0) {
              handleOnClick();
            } else {
              handleOnSampleClick('scale');
            }
            handleClose();
          }}
          sx={{ float: 'right' }}
        >
          {isScaleUser || creditsRemaining > 0 ? 'Proceed' : 'Upgrade'}
        </Button>
      </Box>
    </Modal>
  );
};

// ===============================
// Main component
// ===============================

// Define labels for each filter type used in grouped chips
// const filterTypeLabels: { [key: string]: string } = {
//   location: 'Location',
//   regulation: 'Regulation',
//   marketSize: 'Market Size',
// };

// Helper to get parent macro for a subgroup
const getParentMacro = (
  subgroupValue: string, // This will be a prefixed value like "subgroup-SGCODE"
  allMacroToSubgroups: typeof macro_to_subgroups
): string | null => {
  const unprefixedSubgroup = subgroupValue.startsWith('subgroup-')
    ? subgroupValue.substring('subgroup-'.length)
    : subgroupValue;

  for (const [macro, subgroups] of Object.entries(allMacroToSubgroups)) {
    if (subgroups.includes(unprefixedSubgroup)) {
      return `macro-${macro}`; // Return the prefixed macro
    }
  }
  return null;
};

// Build regionOptions for the dropdown (macro and subgroups), flat array for grouping
const regionOptions = Object.entries(macro_to_subgroups).flatMap(([macro, subgroups]) => {
  const prefixedMacroValue = `macro-${macro}`;
  const macroOption: {
    value: string;
    label: string;
    isExpandable: boolean;
    parentValue: string | null;
    isChild: boolean;
  } = {
    value: prefixedMacroValue,
    label: macro_group_legends[macro],
    isExpandable: true, // Default to true, will be changed if no distinct subgroups
    parentValue: null as string | null,
    isChild: false,
  };

  // Only consider subgroups that are distinct from the macro itself
  const distinctSubgroups = subgroups.filter(sub => sub !== macro);

  if (distinctSubgroups.length === 0 && subgroups.includes(macro)) {
    // Case: NA: ['NA']. The macro itself is the only "group".
    // Macro is not expandable in the region filter as it has no distinct sub-options here.
    macroOption.isExpandable = false;
    return [macroOption]; // Only the macro option is returned
  } else {
    // Case: MEA: ['MENA', 'SSA'] or similar with distinct subgroups
    // Or a macro that has no subgroups listed at all (distinctSubgroups will be empty, subgroups.includes(macro) is false)
    // If distinctSubgroups is empty here, it means the macro has no listed sub-regions other than potentially itself (which was filtered out)
    // or no subregions at all. In such a case, it also shouldn't be expandable.
    if (distinctSubgroups.length === 0) {
      macroOption.isExpandable = false;
    }
    const subOptions = distinctSubgroups.map(sub => {
      const prefixedSubgroupValue = `subgroup-${sub}`;
      return {
        value: prefixedSubgroupValue,
        label: sub_group_legends[sub] || sub,
        isChild: true,
        parentValue: prefixedMacroValue,
        isExpandable: false, // Subgroups in region filter are not expandable
      };
    });
    return [macroOption, ...subOptions];
  }
});

const ExplorerPagePage = ({
  currency,
  handleOnSampleClick,
  isMobileScreen,
  OnBoardingSearchBox,
  isLoggedIn,
  handleSidebarClick,
  handleSidebarListItemClick,
}: {
  currency: string;
  handleOnSampleClick: any;
  isMobileScreen: boolean;
  OnBoardingSearchBox: any;
  isLoggedIn: boolean;
  handleSidebarClick?: any;
  handleSidebarListItemClick?: any;
}) => {
  const urlParams = useRef<URLSearchParams>(new URLSearchParams(window.location.search));
  const urlCountryCode = urlParams.current.get('countryCode');

  const [showFullMap, setShowFullMap] = useState<boolean>(false);
  const [showFullTable, setShowFullTable] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Let the dashboard main scroll horizontally when in full table view so the full table is visible (no clipping)
  const dashboardMainId = 'market-dashboard-main';
  useEffect(() => {
    const main = document.getElementById(dashboardMainId);
    if (!main) return;
    if (showFullTable) {
      main.style.overflowX = 'auto';
    } else {
      main.style.overflowX = '';
    }
    return () => {
      main.style.overflowX = '';
    };
  }, [showFullTable]);
  const [markets, setMarkets] = useState<any[]>([]);
  const [marketSearchInfo, setMarketSearchInfo] = useState<{
    searchName: string;
    countryCode: string;
  } | null>(null);
  const userCountryCode = useSelector(state => (state as any).dashboard.userCountryCode);
  const savedMarketsState = useSelector((state: any) => state.dashboard.savedMarkets);
  const {
    data: selectedList,
    loading: selectedListLoading,
    openSavedMarketsCTAModal,
    isError: selectedListError,
  } = savedMarketsState;

  const [showSavedMarketLoadingError, setShowSavedMarketLoadingError] = useState(false);

  const [userPickedColumnFields, setUserPickedColumnFields] = useState<string[]>([
    ...DEFAULT_SELECTED_COLUMNS_EXPLORER,
  ]);
  const user: User = useSelector(state => (state as any).dashboard.user);

  useEffect(() => {
    const userStrategy = user.userStrategy?.toLowerCase();

    if (userStrategy === 'buy') {
      const newUserPickedColumns = [...userPickedColumnFields];
      newUserPickedColumns.includes('rent_gap') &&
        newUserPickedColumns.splice(newUserPickedColumns.indexOf('rent_gap'), 1);
      !newUserPickedColumns.includes('short_term_rental_yield') &&
        newUserPickedColumns.push('short_term_rental_yield');

      setUserPickedColumnFields(newUserPickedColumns);
    } else if (userStrategy === 'rent') {
      const newUserPickedColumns = [...userPickedColumnFields];
      newUserPickedColumns.includes('short_term_rental_yield') &&
        newUserPickedColumns.splice(newUserPickedColumns.indexOf('short_term_rental_yield'), 1);
      !newUserPickedColumns.includes('rent_gap') && newUserPickedColumns.push('rent_gap');

      setUserPickedColumnFields(newUserPickedColumns);
    }
  }, [user]);

  const defaultMarketConfig = useMemo(
    () => ({
      region: [],
      location: [
        urlCountryCode ? `country-${urlCountryCode.toUpperCase()}` : `country-${userCountryCode}`,
      ],
      // regulation: ['lenient'],
      marketSize: [],
      // budget: {
      //   type: 'buy' as 'buy' | 'rent',
      //   range: { min: 0, max: 0 },
      // },
      sortBy: 'supply',
    }),
    [userCountryCode, urlCountryCode]
  );

  const [currencSearchedMarket, setCurrencSearchedMarket] = useState<{
    region: string[];
    location: string[];
    marketSize: string[];
    sortBy: string;
  }>({
    region: defaultMarketConfig.region,
    location: defaultMarketConfig.location,
    marketSize: defaultMarketConfig.marketSize,
    sortBy: defaultMarketConfig.sortBy,
  });

  // State for filters - selectedLocations now holds country codes and state codes
  const [selectedLocations, setSelectedLocations] = useState<string[]>(
    defaultMarketConfig.location
  );
  const [selectedMarketSizes, setSelectedMarketSizes] = useState<string[]>(
    defaultMarketConfig.marketSize
  );
  const [selectedSortBy, setSelectedSortBy] = useState<string>(defaultMarketConfig.sortBy);

  const [creditDeductModalOpen, setCreditDeductModalOpen] = useState<boolean>(false);

  const [currentRequestType, setCurrentRequestType] = useState<'free' | 'premium'>('free');

  const isFreeUser = !useCanAccessExpertPlan();

  const dispatch = useDispatch();

  const [filtersChanged, setFiltersChanged] = useState(false);

  const [selectedRegions, setSelectedRegions] = useState<string[]>(defaultMarketConfig.region);

  const [showTutorial, setShowTutorial] = useState<boolean>(false);

  const [isUserLoaded, setIsUserLoaded] = useState<boolean>(false);

  const [showExportCreditWarningModal, setShowExportCreditWarningModal] = useState<boolean>(false);

  const isScaleUser = useCanAccessScalePlan();
  const [progress, setProgress] = useState<number>(0);

  const [isApiRateLimited, setIsApiRateLimited] = useState<boolean>(false);

  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const previousLoadingRef = useRef<boolean>(false);
  // Ref to track when filters have been initialized from localStorage or defaults
  const isInitialized = useRef<boolean>(false);
  // Ref to track if initial fetch has been triggered
  const hasInitialFetchRun = useRef<boolean>(false);
  // Ref to store AbortController for canceling previous requests
  const abortControllerRef = useRef<AbortController | null>(null);

  // Helper function to escape CSV cell content
  const escapeCsvCell = (cell: any): string => {
    const cellString = String(cell === null || cell === undefined ? '' : cell);
    const specialCharsRegex = /[,"\n\r]/;
    if (specialCharsRegex.test(cellString)) {
      return `"${cellString.replace(/"/g, '""')}"`;
    }
    return cellString;
  };

  // Helper function to format cell values for CSV export
  const formatCellValueForExport = useCallback(
    (
      value: any,
      metadata:
        | { unit: string | null; is_currency: boolean; data_type: string; display_name: string }
        | undefined
    ): string => {
      if (value === 'locked') return 'Paid plan required';
      if (value === null || value === undefined || value === '-') return '-';

      let formattedValue: string | number = value;
      let isCurrencyField = metadata?.is_currency;
      if (metadata && (metadata.data_type === 'number' || metadata.data_type === 'integer')) {
        try {
          const numberValue = Number(value);
          if (!isNaN(numberValue)) {
            if (isCurrencyField) {
              const valueInSelectedCurrency = convertCurrency(
                String(currency),
                Number(numberValue),
                true
              );
              formattedValue = valueInSelectedCurrency.toFixed(2);
            } else if (metadata?.unit === '%') {
              formattedValue = numberValue.toFixed(1); // Keep as number, e.g., 80.0 or 75.5
            } else {
              formattedValue = numberValue; // For other numbers
            }
          } else {
            formattedValue = '-';
          }
        } catch (e) {
          formattedValue = '-';
        }
      } else {
        formattedValue = String(value);
      }
      return String(formattedValue);
    },
    [currency]
  );

  // Process markets data (defined after helpers, before handleExport)
  const processedMarkets = useMemo((): MarketDataWithBlur[] => {
    let effectiveMarkets = [...markets];
    if (marketSearchInfo) {
      const lockedMarketRow: Omit<MarketDataRow, 'rank'> & { rank: number | string } = {
        rank: 'locked',
        name: formatMarketName({ name: marketSearchInfo.searchName } as Search),
        short_term_rental_yield: 'locked',
        property_price_2bd: 'locked',
        rent_gap: 'locked',
        occupancy_rate: 'locked',
        adr: 'locked',
        revenue: 'locked',
        supply: 'locked',
        regulation: 'locked',
        yoy_supply_change: 'locked',
        yoy_revenue_change: 'locked',
      };
      effectiveMarkets.unshift(lockedMarketRow as MarketDataRow);
    }

    return effectiveMarkets.map(market => {
      const hasLockedValues = Object.values(market).some(value => value === 'locked');
      return {
        ...market,
        name: formatMarketName(market as Search),
        _blurState: hasLockedValues ? 'blurAll' : 'none',
      } as MarketDataWithBlur;
    });
  }, [markets, marketSearchInfo]);

  const handleExportRows = (isPremium: boolean = false) => {
    const headers: string[] = [];
    const dataRows: string[][] = [];

    const currentYearMonthString = new Date().toISOString().slice(0, 7);
    const creditsUsed = user.marketExplorerRowDataExportUsage[currentYearMonthString] || 0;
    const creditsRemaining = MARKET_EXPLORER_EXPORT_LIMIT_ROW_MONTHLY - creditsUsed;

    headers.push('Rank', 'Name', 'Country Code');
    // Use userPickedColumnFields, filter out 'save'
    const exportableKeys = userPickedColumnFields.filter(key => key !== 'save');
    exportableKeys.forEach(key => {
      const columnMeta = MARKET_FINDER_COLUMN_METADATA[key];
      let headerName = columnMeta?.display_name || key;
      if (columnMeta?.is_currency) {
        headerName += ` (${currency})`;
      } else if (columnMeta?.unit === '%') {
        headerName += ' (%)';
      }
      headers.push(headerName);
    });

    const freeExportKeys = ['regulation', 'supply', 'occupancy_rate', 'adr'];

    (!isPremium ? processedMarkets : processedMarkets.slice(0, creditsRemaining)).forEach(
      market => {
        const row: string[] = [
          escapeCsvCell(formatCellValueForExport(market.rank, undefined)),
          escapeCsvCell(formatCellValueForExport(market.name, undefined)),
          escapeCsvCell(formatCellValueForExport(market.country_code, undefined)),
        ];
        exportableKeys.forEach(key => {
          if (!isPremium && !freeExportKeys.includes(key)) {
            row.push(escapeCsvCell(isScaleUser ? 'Unlock to View' : 'Scale Plan Required'));
          } else {
            const metadata = MARKET_FINDER_COLUMN_METADATA[key];
            row.push(escapeCsvCell(formatCellValueForExport(market[key], metadata)));
          }
        });
        dataRows.push(row);
      }
    );

    const csvContent = [headers.join(','), ...dataRows.map(dataRow => dataRow.join(','))].join(
      '\n'
    );

    /*
      TODO
      1. Add the dates in the file name  airbnb_market_stats_2025_12_29
      2. Add _sample if it's sample download
    */
    let file_name = 'airbnb_market_stats';

    makeDownloadableBlob(csvContent, file_name, false, true);

    isPremium &&
      isScaleUser &&
      updateUserExplorerPageRowDataExportUsage(
        Math.min(creditsRemaining, processedMarkets.length),
        dispatch
      );
  };

  // handleExport (defined after processedMarkets and helpers)
  const handleExport = () => {
    setShowExportCreditWarningModal(true);
  };

  const parseSearchConfigKey = (searchConfig: any) => {
    // Search Config
    // {"country_codes":["IN","CA","US"],"us_state_codes":["AR"],"loc_group_codes":["AS"],"regulations":["lenient"],"market_sizes":["big","medium"]}
    // Parsing Rule:
    // demoResultKey = "c-IN-US_s-CA-NY_r-lenient_size-medium-big".toLowerCase()
    // c follows country-code
    // s follows state-code
    // r follows regulation
    // size follows market-size

    const countryCodes = (searchConfig.country_codes || [])
      .map(code => code.toLowerCase())
      .join('-');
    const usStateCodes = (searchConfig.us_state_codes || [])
      .map(code => code.toLowerCase())
      .join('-');
    const locGroupCodes = (searchConfig.loc_group_codes || [])
      .map(code => code.toLowerCase())
      .join('-');
    const regulations = (searchConfig.regulations || []).map(code => code.toLowerCase()).join('-');
    const marketSizes = (searchConfig.market_sizes || []).map(code => code.toLowerCase()).join('-');

    return `loc-${locGroupCodes}_c-${countryCodes}_s-${usStateCodes}_r-${regulations}_size-${marketSizes}`.toLowerCase();
  };

  const parseSearchConfigFromKey = () => {
    const searchConfigFromLocalStorage = localStorage.getItem('last-market-explorer-search-config');
    const searchConfig = searchConfigFromLocalStorage
      ? JSON.parse(searchConfigFromLocalStorage || '{}')
      : null;

    if (!searchConfig) {
      isInitialized.current = true;
      return;
    }

    setSelectedRegions(searchConfig.selectedRegions);
    setSelectedLocations(searchConfig.selectedLocations);
    setSelectedMarketSizes(searchConfig.selectedMarketSizes);
    setSelectedSortBy(searchConfig.selectedSortBy);

    setCurrencSearchedMarket({
      region: searchConfig.selectedRegions,
      location: searchConfig.selectedLocations,
      marketSize: searchConfig.selectedMarketSizes,
      sortBy: searchConfig.selectedSortBy,
    });

    isInitialized.current = true;
  };

  const saveSearchConfig = async (searchConfig: any) => {
    const searchConfigKey = parseSearchConfigKey(searchConfig);
    // Save the search config...
    const response = await callApi(
      SAVE_USER_DATA_LAMBDA_URL,
      { body: { marketExplorerSearchHistory: searchConfigKey } },
      true,
      false,
      '',
      'POST',
      true
    );
    if (response && response.message) {
      console.log('Search config saved successfully');
    }
  };

  const fetchMarkets = useCallback(
    async (deductCredit: boolean = false, isFilterOnlyChange: boolean = false) => {
      setIsApiRateLimited(false);
      
      // Cancel previous request if it exists
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new AbortController for this request
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      try {
        setFiltersChanged(false);
        const requestBody: any = {
          lock_type: 'v2',
          strict_mode: true,
        };

        setIsLoading(true);
        // Reset progress when starting a new request
        setProgress(0);
        // Clear any existing interval to restart progress
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = null;
        }
        // Start new progress interval
        progressIntervalRef.current = setInterval(() => {
          setProgress(prev => {
            if (prev >= 90) {
              return 90; // Cap at 90% until API completes
            }
            // Increment by 2-5% randomly for more realistic feel
            return prev + Math.random() * 3 + 2;
          });
        }, 200);
        setMarkets([]);

        // Handle Region Body
        if (selectedRegions.length > 0) {
          // selectedRegions now contains prefixed macro or sub-group codes
          requestBody.loc_group_codes = selectedRegions.map(r => {
            if (r.startsWith('macro-')) return r.substring('macro-'.length);
            if (r.startsWith('subgroup-')) return r.substring('subgroup-'.length);
            return r; // Should not happen if logic is correct
          });
        }

        // Handle Location Body
        if (selectedLocations.length > 0) {
          const countryCodeArray: Set<string> = new Set();
          const usStatesArray: Set<string> = new Set();
          const regionCodeArray: Set<string> = new Set();
          for (const location of selectedLocations) {
            // location is prefixed, e.g., country-US, state-CA
            if (location.startsWith('state-')) {
              usStatesArray.add(location.substring('state-'.length));
              countryCodeArray.add('US'); // US states imply US country
            } else if (location.startsWith('country-')) {
              countryCodeArray.add(location.substring('country-'.length));
            } else if (location.startsWith('macro-') || location.startsWith('subgroup-')) {
              regionCodeArray.add(location.split('-')[1]);
            }
          }
          requestBody.country_codes = Array.from(countryCodeArray);
          requestBody.us_state_codes = Array.from(usStatesArray);
          requestBody.loc_group_codes = Array.from(regionCodeArray);
        }

        // Handle Market Size Body
        if (selectedMarketSizes.length > 0) {
          requestBody.market_sizes = selectedMarketSizes;
        }

        // Handle Sort By Body
        if (selectedSortBy) {
          requestBody.sort_by = selectedSortBy;
        }

        let isPremiumRequest = deductCredit || isFilterOnlyChange;
        requestBody.is_premium_request = isPremiumRequest;

        setCurrentRequestType(isPremiumRequest ? 'premium' : 'free');

        const searchHistory = user.marketExplorerSearchHistory;
        if (searchHistory.length > 0) {
          const currentSearchConfigKey = parseSearchConfigKey(requestBody);
          if (searchHistory.includes(currentSearchConfigKey)) {
            requestBody.is_premium_request = true;
            setCurrentRequestType('premium');
            isPremiumRequest = true;
          }
        }

        if (!isPremiumRequest && selectedSortBy !== 'supply') {
          setFiltersChanged(_ => true);
          requestBody.sort_by = 'supply';
        }

        const URL = 'https://niyu2lp99j.execute-api.us-east-2.amazonaws.com/default/getBestMarkets';
        const response = await callApi(
          URL,
          { body: requestBody, queryParams: { apiType: 'v2' } },
          true,
          false,
          '',
          'POST',
          true,
          abortController.signal,
          false,
          true
        );

        if(response && response.status == 429) {
          setIsApiRateLimited(true);
        }

        if (response && response.message && response.message.markets) {
          setMarkets(response.message.markets);
          localStorage.setItem(
            'last-market-explorer-search-config',
            JSON.stringify({
              selectedRegions: selectedRegions,
              selectedLocations: selectedLocations,
              selectedMarketSizes: selectedMarketSizes,
              selectedSortBy: selectedSortBy,
            })
          );
          if (
            deductCredit &&
            !user.marketExplorerSearchHistory.includes(parseSearchConfigKey(requestBody))
          ) {
            saveSearchConfig({
              country_codes: requestBody.country_codes,
              us_state_codes: requestBody.us_state_codes,
              loc_group_codes: requestBody.loc_group_codes,
              regulations: requestBody.regulations,
              market_sizes: requestBody.market_sizes,
            });
          }
          setCurrencSearchedMarket({
            region: selectedRegions,
            location: selectedLocations,
            marketSize: selectedMarketSizes,
            sortBy: selectedSortBy,
          });
          if (deductCredit && response.message?.markets?.length > 0) {
            callApi(
              SAVE_USER_DATA_LAMBDA_URL,
              { queryParams: { best_market_finder_usage: true } },
              true,
              false,
              '',
              'GET',
              true
            ).then(() => {
              dispatch(getUserInfo());
            });
          }
        }
        setIsLoading(false);
      } catch (error) {
        // Ignore abort errors - they're expected when canceling previous requests
        if (error instanceof Error && error.name === 'AbortError') {
          return;
        }
        console.error('Error fetching markets:', error);
        setIsLoading(false);
      } finally {
        // Clear the abort controller ref if this was the current request
        if (abortControllerRef.current === abortController) {
          abortControllerRef.current = null;
        }
      }
    },
    [
      selectedRegions,
      selectedLocations,
      selectedMarketSizes,
      selectedSortBy,
      currency,
      dispatch,
      setCurrencSearchedMarket,
      setCurrentRequestType,
      setFiltersChanged,
      setIsLoading,
      setMarkets,
      user,
      // Assuming callApi, convertToUSD, SAVE_USER_DATA_LAMBDA_URL, getUserInfo are stable
    ]
  );

  useEffect(() => {
    if (urlCountryCode) {
      urlParams.current.delete('countryCode');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [urlCountryCode]);

  const isInitializedSavedMarketDataTriggered = useRef<boolean>(false);

  useEffect(() => {
    if (!markets) {
      return;
    }

    if (isLoggedIn && !isInitializedSavedMarketDataTriggered.current) {
      dispatch(initializeSavedMarketsData());
      isInitializedSavedMarketDataTriggered.current = true;
    }

    if (!isLoggedIn) {
      dispatch(setSelectedListLoadingAction(false));
    }
  }, [isLoggedIn, markets]);

  useEffect(() => {
    if (!isInitialized.current) {
      return;
    }
    const isFiltersNotChanged =
      JSON.stringify({
        region: currencSearchedMarket.region,
        location: currencSearchedMarket.location,
        marketSize: currencSearchedMarket.marketSize,
      }) ===
      JSON.stringify({
        region: selectedRegions,
        location: selectedLocations,
        marketSize: selectedMarketSizes,
      });

    const isSortByChanged = selectedSortBy !== currencSearchedMarket.sortBy;

    // Handle initial fetch on first run after initialization
    if (!hasInitialFetchRun.current && isUserLoaded) {
      hasInitialFetchRun.current = true;
      fetchMarkets(false);
      return;
    }

    // Only proceed if something actually changed
    if (!isFiltersNotChanged || isSortByChanged) {
      if (currentRequestType === 'premium' && isFiltersNotChanged && isSortByChanged) {
        fetchMarkets(false, true);
      } else if (!isFiltersNotChanged) {
        fetchMarkets(false);
      } else if (isSortByChanged) {
        setFiltersChanged(true);
      }
    }
  }, [
    selectedSortBy,
    selectedRegions,
    selectedLocations,
    selectedMarketSizes,
    currencSearchedMarket,
    currentRequestType,
    fetchMarkets,
    setFiltersChanged,
    isUserLoaded,
  ]);

  useEffect(() => {
    if (marketSearchInfo && marketSearchInfo.countryCode) {
      setSelectedLocations([marketSearchInfo.countryCode]);
    }
  }, [marketSearchInfo]);

  useEffect(() => {
    setShowSavedMarketLoadingError(selectedListError);
  }, [selectedListError]);

  useEffect(() => {
    if (user.email?.toLowerCase() !== 'trial' || !isLoggedIn) {
      setIsUserLoaded(true);
    }
  }, [user]);

  useEffect(() => {
    parseSearchConfigFromKey();
  }, []);

  // Cleanup: cancel any pending requests on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, []);

  const onFilterChangeAccessCheckMiddleware = (val: string | string[], actionFunction) => {
    if (isFreeUser) {
      handleOnSampleClick('expert');
    } else {
      actionFunction(val);
    }
  };

  // Handlers for FilterDropdown (Moved from FilterBar)
  // Updated handler for country/state selection
  const handleLocationsChange = (newSelection: string[]) => {
    // newSelection contains prefixed values like country-US, state-NY
    let updatedSelection = [...newSelection];

    const includesUSCountry = newSelection.includes('country-US');
    const includesStates = newSelection.some(val => val.startsWith('state-'));

    // If 'country-US' is selected AND specific states are selected, prioritize states by removing 'country-US'
    if (includesUSCountry && includesStates) {
      updatedSelection = updatedSelection.filter(val => val !== 'country-US');
    }
    // If 'country-US' was just added and no states were previously selected OR
    // if 'country-US' is selected and the user is deselecting the last state.
    else if (includesUSCountry) {
      // If 'country-US' is selected, ensure no individual states are in the selection.
      // This handles both adding 'country-US' (clearing states) and ensuring states are cleared if 'country-US' remains.
      updatedSelection = updatedSelection.filter(
        val => !val.startsWith('state-') || val === 'country-US'
      );
    }
    // If states are selected, but 'country-US' is not, ensure 'country-US' is not present.
    // This primarily handles the case where states are selected first, or 'country-US' is deselected while states remain.
    else if (!includesUSCountry && includesStates) {
      updatedSelection = updatedSelection.filter(val => val !== 'country-US');
    }

    // Specific scenario: User just added a state, and 'country-US' was NOT previously selected.
    // Ensure 'country-US' is not automatically added.
    const justAddedState =
      newSelection.some(loc => loc.startsWith('state-')) &&
      !selectedLocations.some(loc => loc.startsWith('state-'));
    if (
      justAddedState &&
      !selectedLocations.includes('country-US') &&
      updatedSelection.includes('country-US')
    ) {
      updatedSelection = updatedSelection.filter(val => val !== 'country-US');
    }

    // Specific scenario: User just added 'country-US', and states WERE previously selected.
    // The logic above (else if (includesUSCountry)) should handle clearing states.

    setSelectedLocations(updatedSelection);
  };
  const handleMarketSizesChange = (val: string[] | string) => {
    if (Array.isArray(val)) setSelectedMarketSizes(val);
  };
  const handleSortByChange = (val: string[] | string) => {
    if (typeof val === 'string') {
      setSelectedSortBy(val);
    }
  };

  // Individual filter removal handlers
  const handleRemoveLocation = (valueToRemove: string) => {
    setSelectedLocations(prev => prev.filter(loc => loc !== valueToRemove));
  };

  const handleRemoveMarketSize = (valueToRemove: string) => {
    setSelectedMarketSizes(prev => prev.filter(size => size !== valueToRemove));
  };

  const handleRegionsChange = (currentFullSelection: string[]) => {
    const finalSelection = new Set<string>();

    const newSelectedMacros = new Set<string>();
    const newSelectedSubregions = new Set<string>();

    currentFullSelection.forEach(item => {
      const unprefixedItemForMacroCheck = item.startsWith('macro-')
        ? item.substring('macro-'.length)
        : item;

      if (macro_to_subgroups[unprefixedItemForMacroCheck] && item.startsWith('macro-')) {
        newSelectedMacros.add(item);
      } else if (item.startsWith('subgroup-')) {
        const parentMacroPrefixed = getParentMacro(item, macro_to_subgroups);
        if (parentMacroPrefixed) {
          newSelectedSubregions.add(item);
        }
      }
    });

    newSelectedMacros.forEach(macro => finalSelection.add(macro));

    newSelectedSubregions.forEach(subregion => {
      const parentMacroPrefixed = getParentMacro(subregion, macro_to_subgroups);
      if (!parentMacroPrefixed || !newSelectedMacros.has(parentMacroPrefixed)) {
        finalSelection.add(subregion);
      }
    });

    setSelectedRegions(Array.from(finalSelection));
  };

  const handleRemoveRegion = (valueToRemove: string) => {
    setSelectedRegions(prev => prev.filter(region => region !== valueToRemove));
  };

  // Progress bar simulation - handles cleanup when loading completes
  useEffect(() => {
    if (!isLoading && previousLoadingRef.current) {
      // Loading just completed
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      setProgress(100);
      // Reset after a brief moment
      const timeoutId = setTimeout(() => setProgress(0), 500);
      return () => clearTimeout(timeoutId);
    }

    // Update previous loading state
    previousLoadingRef.current = isLoading;

    return () => {
      // Cleanup on unmount
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    };
  }, [isLoading]);


  const TableToolbarComponent = () => {
    return (
      <Grid container item xs={12} spacing={1}>
        <Grid container item xs={12}>
          <FilterBar
            selectedRegions={selectedRegions}
            regionOptions={regionOptions}
            handleRegionsChange={handleRegionsChange}
            selectedLocations={selectedLocations}
            selectedMarketSizes={selectedMarketSizes}
            handleLocationsChange={handleLocationsChange}
            handleMarketSizesChange={handleMarketSizesChange}
            countryOptions={countryOptions}
            marketSizeOptions={marketSizeOptions}
            onFilterChangeAccessCheckMiddleware={onFilterChangeAccessCheckMiddleware}
            openSearchModal={() =>
              !isFreeUser ? setCreditDeductModalOpen(true) : handleOnSampleClick('expert')
            }
            isMobileScreen={isMobileScreen}
            OnBoardingSearchBox={OnBoardingSearchBox}
            setMarketSearchInfo={setMarketSearchInfo}
            isFreeUser={isFreeUser}
            currency={currency}
            handleSidebarClick={handleSidebarClick}
            handleSidebarListItemClick={handleSidebarListItemClick}
            onExport={handleExport}
            userPickedColumnFields={userPickedColumnFields}
            onUserPickedFieldsChange={setUserPickedColumnFields}
            showFullTable={showFullTable}
            setShowFullTable={setShowFullTable}
          />
        </Grid>

        <Grid container item xs={12}>
          <SelectedFiltersDisplay
            selectedRegions={selectedRegions}
            regionOptions={regionOptions}
            handleRemoveRegion={handleRemoveRegion}
            selectedLocations={selectedLocations}
            selectedMarketSizes={selectedMarketSizes}
            selectedSortBy={selectedSortBy}
            countryOptions={countryOptions}
            marketSizeOptions={marketSizeOptions}
            sortByOptions={sortByOptions}
            findLabel={findLabel}
            handleRemoveLocation={handleRemoveLocation}
            handleRemoveMarketSize={handleRemoveMarketSize}
            handleSortByChange={handleSortByChange}
            isMobileScreen={isMobileScreen}
            isFreeUser={isFreeUser}
            handleOnSampleClick={handleOnSampleClick}
            currency={currency}
            userPickedColumnFields={userPickedColumnFields}
          />
        </Grid>
      </Grid>
    );
  };

  const TableComponent = () => {
    return (
      <Grid container item xs={12}>
        {/* Pass processed data to the grid */}
        {isLoading ? (
          <Skeleton variant="rectangular" height={400} width="100%" animation="wave" />
        ) : (
          <MarketDataGrid
            data={processedMarkets}
            metadata={MARKET_FINDER_COLUMN_METADATA}
            isFreeUser={isFreeUser}
            isLoggedIn={isLoggedIn}
            handleOnSampleClick={handleOnSampleClick}
            currency={currency}
            currentRequestType={currentRequestType}
            openCreditDeductModal={() => setCreditDeductModalOpen(true)}
            selectedList={selectedListLoading ? 'loading' : selectedList}
            selectedListError={selectedListError}
            showSavedMarketLoadingError={showSavedMarketLoadingError}
            setShowSavedMarketLoadingError={setShowSavedMarketLoadingError}
            handleSidebarClick={handleSidebarClick}
            handleSidebarListItemClick={handleSidebarListItemClick}
            selectedSortBy={selectedSortBy}
            userPickedColumnFields={userPickedColumnFields}
            showFullTable={showFullTable}
          />
        )}
      </Grid>
    );
  };

  const MapToolbarComponent = () => {
    return (
      <>
        {!isMobileScreen && (
          <Grid container item xs={12} justifyContent="flex-end">
            <ShowFullMapButton showFullMap={showFullMap} setShowFullMap={setShowFullMap} />
          </Grid>
        )}
      </>
    );
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography
            variant="h2"
            sx={{
              fontSize: '24px',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: '2px',
            }}
          >
            Market Explorer
            <TooltipNew content="Click to see the 3 minutes tutorial video on how to use this feature.">
              <IconButton color="primary" onClick={() => setShowTutorial(true)}>
                <HelpOutlineRoundedIcon />
              </IconButton>
            </TooltipNew>
          </Typography>
        </Grid>

        {isApiRateLimited && (
          <Grid item xs={12}>
            <RateLimitAlert />
          </Grid>
        )}

        {isLoading && (
          <Grid item xs={12}>
            <Box sx={{ width: '100%' }}>
              <Box
                sx={{
                  mb: 1,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant="body2">
                  Loading markets... This may take up to 15 seconds
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  {Math.round(progress)}%
                </Typography>
              </Box>
              <LinearProgress variant="determinate" value={progress} />
            </Box>
          </Grid>
        )}

        <Grid container item xs={12}>
          <TwoPaneMapTableLayout
            mapToolbarComponent={<MapToolbarComponent />}
            mapComponent={
              <MapComponent
                markets={markets}
                isLoading={isLoading}
                isMobileScreen={isMobileScreen}
                showFullMap={showFullMap}
                currentRequestType={currentRequestType}
                selectedSortBy={selectedSortBy}
                currency={currency}
              />
            }
            tableToolbarComponent={<TableToolbarComponent />}
            tableComponent={<TableComponent />}
            sortComponent={<></>}
            showFullMap={showFullMap}
            showFullTable={showFullTable}
          />
        </Grid>
      </Grid>

      {filtersChanged && (
        <AutohideSnackbar
          message="Please unlock the search to enable filter changes."
          type="warning"
          autoHideDuration={5000}
          handleOnCloseSideEffect={() => {
            setFiltersChanged(false);
          }}
        />
      )}

      {creditDeductModalOpen && (
        <CreditWarningModal
          open={creditDeductModalOpen}
          handleClose={() => setCreditDeductModalOpen(false)}
          handleOnClick={() => fetchMarkets(true)}
          handleOnSampleClick={handleOnSampleClick}
        />
      )}

      {showTutorial && (
        <TutorialModal
          open={showTutorial}
          onClose={() => setShowTutorial(false)}
          type="market_explorer"
          noProgressBar={true}
        />
      )}

      {showExportCreditWarningModal && (
        <MarketExportModal
          open={showExportCreditWarningModal}
          handleClose={() => setShowExportCreditWarningModal(false)}
          handleExportRows={handleExportRows}
          handleOnSampleClick={handleOnSampleClick}
          numberOfRows={processedMarkets.length}
          handleExportRowsFree={() => handleExportRows(false)}
          setCreditDeductModalOpen={setCreditDeductModalOpen}
          currentRequestType={currentRequestType}
        />
      )}
    </>
  );
};

export default ExplorerPagePage;
